import { createIsomorphicFn } from '@tanstack/react-start';
import { getRequest } from '@tanstack/react-start/server';
import { prerenderBuildInput, prerenderMarker } from 'virtual:anyapi-prerender-input';

const PRERENDER_HEADER = 'x-anyapi-prerender';

/**
 * Gateway payloads fetched once per build and baked into the SSR bundle.
 *
 * The marketing routes prerender with `crawlLinks`, so a route loader that
 * fetches per page multiplies into hundreds of gateway calls from one address
 * during the crawl. Fetching each payload once at build and handing it to the
 * marked crawl requests is how the catalog already avoids that, and the same
 * reasoning covers every payload a prerendered page needs.
 */
export type PrerenderInputKey = 'catalog' | 'benchmarks' | 'networkUsage' | 'apiSpec';

export type PrerenderBuildInput = Readonly<Partial<Record<PrerenderInputKey, unknown>>>;

export const selectPrerenderInput = (
  marker: string | null,
  expectedMarker: string | undefined,
  buildInput: PrerenderBuildInput | undefined,
  key: PrerenderInputKey,
): unknown => (expectedMarker && marker === expectedMarker ? buildInput?.[key] : undefined);

/**
 * Returns immutable build input only to marked prerender requests. Normal SSR
 * and browser requests always return undefined and fetch live.
 */
export const prerenderInput = createIsomorphicFn()
  .client((_key: PrerenderInputKey): unknown => undefined)
  .server(
    (key: PrerenderInputKey): unknown =>
      selectPrerenderInput(
        getRequest().headers.get(PRERENDER_HEADER),
        prerenderMarker,
        prerenderBuildInput as PrerenderBuildInput | undefined,
        key,
      ),
  );

/**
 * True only while the build crawls routes to emit static HTML. Discovery is a
 * hard precondition there - `prerender.failOnError` must fail the build rather
 * than bake an empty catalog into shipped pages - and a tolerated enhancement
 * everywhere else.
 */
export const isPrerenderRequest = createIsomorphicFn()
  .client((): boolean => false)
  .server(
    (): boolean =>
      Boolean(prerenderMarker)
      && getRequest().headers.get(PRERENDER_HEADER) === prerenderMarker,
  );
